import { useCallback, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { YStack, XStack, View, Text, Spinner } from "tamagui";
import {
  RiCheckLine,
  RiErrorWarningLine,
  RiTimeLine,
  RiShieldCheckLine,
  RiBankCardLine,
  RiFileList3Line,
  RiNotification3Line,
  RiLineFill,
} from "react-icons/ri";

import Header from "@components/layout/Header";
import RadiusContentWrapper from "@components/layout/RadiusContentWrapper";
import { colors } from "@styles/colors";
import { formatGasTitle } from "@utils/func/gasTitle";
import { appToast } from "@components/Toast";
import { getPayLinkToken, storePayLinkToken, clearPayLinkToken } from "@utils/payLink";
import { useApplePay } from "@hooks/useApplePay";
import ApplePayButton from "@components/payment/ApplePayButton";
import { getRuntimeLiffId } from "@utils/line/getLiffId";

import {
  handleAPIGetPayLinkStatus,
  handleAPIOpenPayLinkPage,
  handleAPICreatePayLinkIntent,
  handleAPIConfirmPayLinkIpassTwqr,
  type PayLinkStatus,
  type PaymentViewer,
} from "../Account/API/handlePaymentAPI";
import { AddFriendCard, useOaFriendship } from "./AddFriend";
import OneTimeCardForm, { type OneTimeCardFormHandle, type OneTimeCardState } from "./OneTimeCardForm";
import { rememberThreeDsOrder } from "@utils/payThreeDsReturn";
import { readIpassTwqrReturn } from "@utils/payIpassReturn";

const ipassMoneyLogo = require("@styles/images/payment/ipass_money.png") as string;

const AUTO_IPASS_KEY = (orderId: string) => `paylink_auto_ipass:${orderId}`;
/** 網址帶 auto=ipass 就記進 sessionStorage（綁單號）；沒帶就看之前有沒有記過（頁面重開的情況） */
function readAutoIpassFlag(orderId: string | undefined): boolean {
  if (!orderId) return false;
  const fromQuery = new URLSearchParams(window.location.search).get("auto") === "ipass";
  try {
    if (fromQuery) {
      sessionStorage.setItem(AUTO_IPASS_KEY(orderId), "1");
      return true;
    }
    return sessionStorage.getItem(AUTO_IPASS_KEY(orderId)) === "1";
  } catch {
    return fromQuery;
  }
}
function clearAutoIpassFlag(orderId: string | undefined): void {
  if (!orderId) return;
  try {
    sessionStorage.removeItem(AUTO_IPASS_KEY(orderId));
  } catch {
    // sessionStorage 不可用（隱私模式等）：沒存就不用清
  }
}
import { useCusAuth } from "@utils/store/auth/useCusAuth";
import { JoinFamilyCard } from "./JoinFamilyCard";
import { OaRedirectCountdown, useJustPaid } from "./OaRedirectCountdown";

import { notPayableHint } from "./notPayableHint";
import { PaymentProcessingView, usePaymentResultPoll } from "./PaymentProcessing";
import { PaymentLockCountdownNote, usePaymentLockCountdown } from "./PaymentLockCountdown";

type View_ = "loading" | "error" | "link_expired" | "paid" | "not_payable" | "payable" | "processing" | "success";

/**
 * 是否顯示 Apple Pay 不可用的實際原因（`[dev] {reason}`）：本機開發（NODE_ENV=development）
 * 或部署在 dev 環境的站（REACT_APP_SENTRY_ENVIRONMENT=dev）才顯示；pre-prod/prod 不受影響。
 */
const showApReason = process.env.NODE_ENV === "development" || process.env.REACT_APP_SENTRY_ENVIRONMENT === "dev";

/**
 * 後端錯誤訊息不可直接顯示；統一對應中文友善提示。
 * PAY_TOKEN_EXPIRED／PAY_TOKEN_USED 這兩個連結本身失效的狀況要優先判斷（呼叫端會另外切畫面，
 * 這裡的文字只在還是走一般 toast 的情況下用得到），再落到 TapPay 拒絕／prime 過期等一般訊息。
 */
/**
 * 一卡通導回的 confirm（A2）同一個 pay token 只打一次。React 18 StrictMode 在開發模式會把掛載 effect
 * 跑兩次，兩個 confirm 同時到後端時第二個會撞到第一個正在做 A2 的列鎖、拿到「付款中」，畫面就停在確認中。
 * 用模組層的 in-flight 快取讓第二次掛載直接等第一次的結果；結束後清掉，重新整理仍會再確認一次。
 */
const ipassConfirmInflight = new Map<string, ReturnType<typeof handleAPIConfirmPayLinkIpassTwqr>>();
const confirmIpassTwqrOnce = (pt: string) => {
  const hit = ipassConfirmInflight.get(pt);
  if (hit) return hit;
  const p = handleAPIConfirmPayLinkIpassTwqr(pt).finally(() => ipassConfirmInflight.delete(pt));
  ipassConfirmInflight.set(pt, p);
  return p;
};

const mapApplePayErr = (error: unknown, method: "apple_pay" | "credit_card" | "ipass_twqr" = "apple_pay"): string => {
  const raw = typeof error === "string" ? error : "";
  if (/PAY_TOKEN_EXPIRED/.test(raw)) return "連結已過期，請請配送員重新出示付款碼";
  if (/PAY_TOKEN_USED/.test(raw)) return "此訂單已完成付款";
  // 請款鎖到期（開頁超過 5 分鐘）：要排在下面泛用的 expired 前面，不然會被當成卡片資料逾時
  if (/PAYMENT_REQUEST_EXPIRED/.test(raw)) return "付款連結已過期（開啟超過 5 分鐘），請聯絡配送員重新請款";
  if (/IPASS_TWQR_UNAVAILABLE/.test(raw)) return "一卡通 MONEY 付款暫時無法使用，請改用其他方式";
  if (method === "ipass_twqr") return /already|active payment/i.test(raw) ? "付款處理中，請稍候" : "無法開啟一卡通付款，請稍後再試";
  if (/prime|expired/i.test(raw)) return method === "credit_card" ? "卡片資料已逾時，請再按一次付款" : "付款授權已逾時，請再按一次 Apple Pay";
  if (/already|active payment/i.test(raw)) return "付款處理中，請稍候";
  return "付款失敗，請稍後再試";
};

/**
 * 付款連結模式 /pay/:orderId?pt=<token>：訪客付款（2026-10-19）。
 * 未註冊、不是這個家庭的人、沒有付款權限的成員都在這裡付：信用卡單次刷卡（3D 驗證、不留卡）
 * 或 Apple Pay。整頁不依賴 cusJwtToken／chooseIdentity，付款權限由 pay_token 決定。
 * 本人／可付款成員由 PayLinkGate 分去 LINE 模式（可用已綁的卡），不會進來。
 */
export const PayLinkView = ({ viewer = "guest" }: { viewer?: PaymentViewer }) => {
  const { orderId } = useParams();
  // 凍結掛載當下的 pt：讀完後會 replaceState 把 query 清掉，若每次 render 重讀 window.location.search，
  // 之後任何一次重新渲染都會讀到 pt=null（除非已存入 sessionStorage 備援），導致 effect 重跑並把
  // 已經載入好的畫面覆蓋成 error。
  const [pt] = useState<string | null>(() => getPayLinkToken());

  const [view, setView] = useState<View_>("loading");
  const [errMsg, setErrMsg] = useState("");
  const [status, setStatus] = useState<PayLinkStatus | null>(null);
  // 付款成功當下的資訊存在這個分頁：外層版面重新掛載本頁時，仍顯示「付款完成＋可選註冊」，
  // 不會變成重新讀取後的「已付款」畫面
  const successKey = `pay_link_success:${orderId ?? ""}`;
  const [successInfo, setSuccessInfoState] = useState<{ amount: number; label: string; time: string } | null>(() => {
    try {
      const raw = window.sessionStorage.getItem(successKey);
      return raw ? (JSON.parse(raw) as { amount: number; label: string; time: string }) : null;
    } catch {
      return null;
    }
  });
  const setSuccessInfo = (info: { amount: number; label: string; time: string }) => {
    setSuccessInfoState(info);
    try {
      window.sessionStorage.setItem(successKey, JSON.stringify(info));
    } catch {
      // sessionStorage 不可用：只是重新掛載後改顯示「已付款」
    }
  };
  const [cardSubmitting, setCardSubmitting] = useState(false);
  // 是不是瓦斯行官方帳號的好友：只有在 LINE 裡（LIFF 已登入）問得到；問不到（LINE 外開）就是 null、不顯示加好友
  const isOaFriend = useOaFriendship();
  const showAddFriend = isOaFriend === false && !!status?.pay_link.add_friend_url;
  // 剛付完（含 3D 驗證回來）才倒數回官方帳號、才提議已註冊的非成員加入家庭（2026-10-28）
  const justPaid = useJustPaid(view, orderId);
  const { cusJwtToken } = useCusAuth();
  const cardRef = useRef<OneTimeCardFormHandle>(null);
  const [cardState, setCardState] = useState<OneTimeCardState>({ sdkReady: false, canPay: false, gettingPrime: false });
  const onCardStateChange = useCallback((st: OneTimeCardState) => setCardState(st), []);
  // Apple Pay 按鈕在 intent 呼叫期間鎖住：ap.state 在 pay() 的 finally 就已經回到 "ready"
  // （見 useApplePay.pay），此後到 handleAPICreatePayLinkIntent 回來之前有一段空窗，
  // 連點會打出多筆 intent 請求；用獨立於 ap.state 的 submitting 蓋住整段 handler。
  const [submitting, setSubmitting] = useState(false);
  // 一卡通 MONEY 按鈕鎖住：intent 回來前連點會打出多筆（同 Apple Pay 的 submitting）
  const [ipassSubmitting, setIpassSubmitting] = useState(false);
  // 從 iPASS MONEY 付完導回：網址帶 orderNumber=IPQ…（一卡通加的）。凍結掛載當下的值，
  // 等一下 replaceState 會把它從網址列清掉。
  const [ipassReturn] = useState<string | null>(() => readIpassTwqrReturn(window.location.search));
  // 從 LINE 模式頁外開過來（auto=ipass）：一載入可付款就自動打 A1 跳 iPASS MONEY，客戶不用再按一次。
  // 跟 pt 一樣存一份在 sessionStorage：LIFF 初始化可能讓頁面重開一次（網址列的 query 已被 replaceState 清掉），
  // 第二次載入也要知道要自動。打過一次就清掉，跳回來沒成功就讓客戶自己按。
  const [autoIpass] = useState<boolean>(() => readAutoIpassFlag(orderId));
  const autoIpassFired = useRef(false);
  /** 一卡通導回時標「付款完成」版用的方式名稱（跟 Apple Pay／信用卡那兩條路一致） */
  const ipassSuccess = (amount: number) => {
    setSuccessInfo({ amount, label: "iPASS MONEY", time: new Date().toLocaleString("zh-TW", { hour12: false }) });
    setView("success");
  };
  // 開頁（POST pay-link/open，開始請款鎖）只在第一次載入打一次：StrictMode 重跑 effect 時共用同一個請求
  const openRequest = useRef<{ token: string; promise: ReturnType<typeof handleAPIGetPayLinkStatus> } | null>(null);
  const recheckingLock = useRef(false);

  useEffect(() => {
    if (!pt) {
      setErrMsg("付款連結無效");
      setView("error");
      return;
    }
    // 拿到 pt 就先存進 sessionStorage 備援（key 綁 orderId），讓等一下的 replaceState
    // 清掉網址列後，重新整理或任何重新求值都還讀得到；付款完成／連結失效時清掉。
    if (orderId) storePayLinkToken(orderId, pt);
    let alive = true;
    // 第一次載入打 open（開始請款鎖）；open 失敗退回 GET（舊後端沒有這支等），頁面照常。
    // 401（連結本身過期／用過）是 pay_token 的結果，GET 也一樣，直接用
    const loadStatus = () => {
      if (openRequest.current) return openRequest.current.token === pt ? openRequest.current.promise : handleAPIGetPayLinkStatus(pt);
      const promise = handleAPIOpenPayLinkPage(pt).then((opened) =>
        opened.state === "success" || opened.status === 401 ? opened : handleAPIGetPayLinkStatus(pt),
      );
      openRequest.current = { token: pt, promise };
      return promise;
    };
    (async () => {
      // 一卡通導回：先請後端向一卡通確認結果（A2），再照一般狀態顯示；沒導回就開頁（開始請款鎖）＋查狀態。
      // confirm 失敗（網路等）退回開頁查狀態——交易仍在進行中，對帳排程會補做 A2。
      const r = ipassReturn ? await confirmIpassTwqrOnce(pt).then((c) => (c.state === "success" ? c : loadStatus())) : await loadStatus();
      // 讀完就把 pay_token／orderNumber 從網址列移除，避免殘留在瀏覽器歷史 / 分享截圖外流（sessionStorage 已存一份備援）。
      window.history.replaceState(null, "", window.location.pathname);
      if (!alive) return;

      if (r.state !== "success") {
        const raw = typeof r.error === "string" ? r.error : "";
        if (r.status === 401 && /PAY_TOKEN_EXPIRED/.test(raw)) {
          if (orderId) clearPayLinkToken(orderId);
          setView("link_expired");
          return;
        }
        if (r.status === 401 && /PAY_TOKEN_USED/.test(raw)) {
          if (orderId) clearPayLinkToken(orderId);
          setView("paid");
          return;
        }
        setErrMsg("無法載入付款資訊");
        setView("error");
        return;
      }

      setStatus(r.data);
      if (r.data.paid) {
        if (orderId) clearPayLinkToken(orderId);
        // 剛從 iPASS MONEY 付完回來：顯示「付款完成」版（含可選註冊），付款方式標一卡通
        if (ipassReturn && !successInfo) {
          ipassSuccess(r.data.amount ?? r.data.transaction?.amount ?? 0);
          return;
        }
        setView(successInfo ? "success" : "paid");
        return;
      }
      if (r.data.payment_in_progress) {
        setView("processing");
        return;
      }
      if (!r.data.payable) {
        setView("not_payable");
        return;
      }
      // 從一卡通回來卻又可付款：那筆沒成功（取消、逾時、餘額不足），說一聲再讓客戶重選
      if (ipassReturn) appToast.error("一卡通付款沒有完成，請再試一次或改用其他方式");
      setView("payable");
    })();
    return () => {
      alive = false;
    };
  }, [pt, orderId]);

  // Apple Pay 狀態機需固定呼叫（不可依 view 條件式呼叫 hook）；用 enabled 在非 payable 畫面前保持閒置。
  const ap = useApplePay({
    amount: status?.amount ?? null,
    label: status?.pay_link.supplier_name ?? "",
    enabled: view === "payable",
  });

  const onApplePay = async () => {
    if (!pt || !status || submitting) return;
    setSubmitting(true);
    try {
      const r = await ap.pay();
      if (!r.ok) {
        appToast.error(mapApplePayErr(r.msg));
        return;
      }
      const res = await handleAPICreatePayLinkIntent(pt, status.platform_order_id, r.prime);
      // 202：銀行還沒回，結果確認中——不是成功，也不能讓客戶再付一次
      if (res.state === "success" && res.data?.status !== "captured") {
        setView("processing");
        return;
      }
      if (res.state === "success") {
        // 不清掉 pt 備援：外層重新掛載時還要靠它留在付款連結模式（後端允許用過的 token 查結果）
        setSuccessInfo({
          amount: status.amount ?? res.data?.amount ?? 0,
          label: "Apple Pay",
          time: new Date().toLocaleString("zh-TW", { hour12: false }),
        });
        setView("success");
        return;
      }
      const raw = typeof res.error === "string" ? res.error : "";
      if (/PAY_TOKEN_EXPIRED/.test(raw)) {
        if (orderId) clearPayLinkToken(orderId);
        setView("link_expired");
        return;
      }
      if (/PAY_TOKEN_USED/.test(raw)) {
        // 已透過此連結完成過付款（例如重覆送出）；視為已付款，直接顯示 paid 畫面
        // （沿用已載入的 status.amount/label，比純載入期的 PAY_TOKEN_USED 分支資訊更完整）。
        if (orderId) clearPayLinkToken(orderId);
        setView("paid");
        return;
      }
      appToast.error(mapApplePayErr(raw));
      if (/PAYMENT_REQUEST_EXPIRED/.test(raw)) void recheckStatus();
    } catch {
      appToast.error("付款失敗，請稍後再試");
    } finally {
      setSubmitting(false);
    }
  };

  const onCardPrime = async (prime: string) => {
    if (!pt || !status || cardSubmitting) return;
    setCardSubmitting(true);
    try {
      const res = await handleAPICreatePayLinkIntent(pt, status.platform_order_id, prime, "credit_card");
      if (res.state === "success" && res.data?.payment_url) {
        // 3D 驗證：跳去銀行頁；驗證完 TapPay 帶回 /pay/3ds-result，那頁再導回本單的付款連結
        if (orderId) rememberThreeDsOrder(orderId);
        window.location.href = res.data.payment_url;
        return;
      }
      if (res.state === "success" && res.data?.status !== "captured") {
        setView("processing");
        return;
      }
      if (res.state === "success") {
        // 同上：不清 pt 備援
        const last4 = res.data?.card?.lastfour;
        setSuccessInfo({
          amount: status.amount ?? res.data?.amount ?? 0,
          label: last4 ? `信用卡 •••• ${last4}` : "信用卡",
          time: new Date().toLocaleString("zh-TW", { hour12: false }),
        });
        setView("success");
        return;
      }
      const raw = typeof res.error === "string" ? res.error : "";
      if (/PAY_TOKEN_EXPIRED/.test(raw)) {
        if (orderId) clearPayLinkToken(orderId);
        setView("link_expired");
        return;
      }
      if (/PAY_TOKEN_USED/.test(raw)) {
        if (orderId) clearPayLinkToken(orderId);
        setView("paid");
        return;
      }
      appToast.error(mapApplePayErr(raw, "credit_card"));
      if (/PAYMENT_REQUEST_EXPIRED/.test(raw)) void recheckStatus();
    } catch {
      appToast.error("付款失敗，請稍後再試");
    } finally {
      setCardSubmitting(false);
    }
  };

  /**
   * 一卡通 MONEY（TWQR 單筆）：後端 A1 拿支付頁網址 → 整頁導去 iPASS MONEY 付這一筆；
   * 付完一卡通導回 /pay/{單號}?orderNumber=…，上面的 effect 看到就先 confirm 再顯示結果。
   */
  const onIpassPay = async () => {
    if (!pt || !status || ipassSubmitting) return;
    setIpassSubmitting(true);
    try {
      const res = await handleAPICreatePayLinkIntent(pt, status.platform_order_id, null, "ipass_twqr");
      if (res.state === "success" && res.data?.payment_url) {
        window.location.href = res.data.payment_url;
        return;
      }
      if (res.state === "success") {
        // 沒拿到支付頁網址但交易建了（理論上不會）：當處理中，讓輪詢收尾
        setView("processing");
        return;
      }
      const raw = typeof res.error === "string" ? res.error : "";
      if (/PAY_TOKEN_EXPIRED/.test(raw)) {
        if (orderId) clearPayLinkToken(orderId);
        setView("link_expired");
        return;
      }
      if (/PAY_TOKEN_USED/.test(raw)) {
        if (orderId) clearPayLinkToken(orderId);
        setView("paid");
        return;
      }
      appToast.error(mapApplePayErr(raw, "ipass_twqr"));
    } catch {
      appToast.error("無法開啟一卡通付款，請稍後再試");
    } finally {
      setIpassSubmitting(false);
    }
  };

  useEffect(() => {
    if (!autoIpass || autoIpassFired.current || view !== "payable" || !status || ipassReturn) return;
    if (!status.pay_link.ipass_twqr_enabled) return;
    autoIpassFired.current = true;
    clearAutoIpassFlag(orderId);
    void onIpassPay();
    // onIpassPay 每次 render 重建，依賴只看「可付款了沒」；fired 旗標保證只跑一次
  }, [autoIpass, view, status, ipassReturn]);

  usePaymentResultPoll(
    view === "processing",
    async () => {
      if (!pt) return null;
      const r = await handleAPIGetPayLinkStatus(pt);
      return r.state === "success" ? r.data : null;
    },
    (st) => {
      setStatus(st as PayLinkStatus);
      if (st.paid) {
        if (orderId) clearPayLinkToken(orderId);
        // 一卡通導回後 confirm 撞到「付款中」才進到這裡輪詢：結果出來一樣給「付款完成」版
        if (ipassReturn && !successInfo) ipassSuccess(st.amount ?? st.transaction?.amount ?? 0);
        else setView("paid");
        return;
      }
      appToast.error("付款沒有成功，請再試一次");
      setView(st.payable ? "payable" : "not_payable");
    },
  );

  // 重新查付款狀態（GET，不延長請款鎖）並切畫面：請款鎖到期、或付款時後端回請款已過期時用
  async function recheckStatus() {
    if (!pt || recheckingLock.current) return;
    recheckingLock.current = true;
    try {
      const r = await handleAPIGetPayLinkStatus(pt);
      if (r.state !== "success") return;
      setStatus(r.data);
      if (r.data.paid) {
        if (orderId) clearPayLinkToken(orderId);
        setView("paid");
      } else if (r.data.payment_in_progress) setView("processing");
      else if (!r.data.payable) setView("not_payable");
    } finally {
      recheckingLock.current = false;
    }
  }

  // 請款鎖倒數：時間到重新查一次，後端已取消請款就會切到「連結已失效」
  const lockRemaining = usePaymentLockCountdown(
    status?.payment_lock_expires_at,
    view === "payable",
    () => {
      void recheckStatus();
    },
    submitting || cardSubmitting,
  );

  const headerNode = (title: string, sub?: string) => (
    <Header>
      <Text color={colors.light} fontSize={22} fontWeight="800" mb="$1.5">
        {title}
      </Text>
      {sub ? (
        <Text color="rgba(255,255,255,0.85)" fontSize={12}>
          {sub}
        </Text>
      ) : null}
    </Header>
  );

  if (view === "loading") {
    return (
      <YStack height="100dvh" width="100vw" background={colors.bg}>
        {headerNode("付款")}
        <YStack f={1} ai="center" jc="center">
          <Spinner size="large" color={colors.primary} />
        </YStack>
      </YStack>
    );
  }

  if (view === "error") {
    return (
      <YStack height="100dvh" width="100vw" background={colors.bg}>
        {headerNode("付款")}
        <YStack f={1} ai="center" jc="center" gap="$2" px="$6">
          <RiErrorWarningLine size={40} color={colors.hint} />
          <Text fontSize={14} color={colors.sub} ta="center">
            {errMsg}
          </Text>
        </YStack>
      </YStack>
    );
  }

  if (view === "processing") {
    return <PaymentProcessingView header={headerNode("付款")} />;
  }

  if (view === "link_expired") {
    return (
      <YStack height="100dvh" width="100vw" background={colors.bg}>
        {headerNode("付款")}
        <YStack f={1} ai="center" jc="center" gap="$2" px="$6">
          <RiTimeLine size={40} color={colors.hint} />
          <Text fontSize={15} fontWeight="700" color={colors.text} ta="center">
            連結已過期，請請配送員重新出示付款碼
          </Text>
        </YStack>
      </YStack>
    );
  }

  if (view === "not_payable") {
    const hint = notPayableHint(status);
    return (
      <YStack height="100dvh" width="100vw" background={colors.bg}>
        {headerNode("付款")}
        <YStack f={1} ai="center" jc="center" gap="$2" px="$6">
          <RiErrorWarningLine size={40} color={colors.hint} />
          <Text fontSize={15} fontWeight="700" color={colors.text}>
            這筆訂單尚未開放線上付款
          </Text>
          <Text fontSize={12} color={colors.hint} ta="center">
            {hint}
          </Text>
        </YStack>
      </YStack>
    );
  }

  if (view === "success" || view === "paid") {
    const tx = status?.transaction;
    const amount = view === "success" ? (successInfo?.amount ?? 0) : (tx?.amount ?? status?.amount ?? 0);
    const label =
      view === "success" ? (successInfo?.label ?? "Apple Pay") : tx?.lastfour ? `信用卡 •••• ${tx.lastfour}` : (status?.label ?? "Apple Pay");
    const timeText =
      view === "success" ? (successInfo?.time ?? "") : tx?.paid_at ? new Date(tx.paid_at).toLocaleString("zh-TW", { hour12: false }) : "";
    // PAY_TOKEN_USED 錯誤路徑沒有 status（沒打成功過 API），此時無法得知 return_url，退回「可以關閉此頁」文案。
    const returnUrl = status?.pay_link.return_url ?? null;
    // 只對未註冊、這次剛用付款連結付完的人提議註冊自己的帳號（不加入付款的這個家庭）
    const registerLiffId = getRuntimeLiffId();
    const offerRegister = view === "success" && viewer === "guest" && !!registerLiffId;
    return (
      <YStack height="100dvh" width="100vw" background={colors.bg}>
        {headerNode("付款")}
        <RadiusContentWrapper>
          <YStack ai="center" gap="$3" pt="$6">
            <View w={72} h={72} br={36} bg="#eafaf0" ai="center" jc="center">
              <RiCheckLine size={36} color={colors.green} />
            </View>
            <Text fontSize={17} fontWeight="800" color={colors.text}>
              已付款
            </Text>
            <Text fontSize={12} color={colors.sub} ta="center">
              {offerRegister
                ? "這筆已經付好了，瓦斯行會立刻收到通知"
                : view === "success"
                  ? "已通知瓦斯行，訂單狀態同步更新"
                  : timeText
                    ? `${timeText} 已完成付款`
                    : "這筆訂單已完成付款"}
            </Text>
            <YStack bg={colors.light} bw={1} bc={colors.borderLight} br={12} px="$4" py="$3" width="100%" gap="$1.5">
              <XStack jc="space-between">
                <Text fontSize={13} color={colors.sub}>
                  付款金額
                </Text>
                <Text fontSize={13} fontWeight="700" color={colors.text} style={{ fontVariantNumeric: "tabular-nums" }}>
                  NT$ {amount.toLocaleString()}
                </Text>
              </XStack>
              <XStack jc="space-between">
                <Text fontSize={13} color={colors.sub}>
                  付款方式
                </Text>
                <Text fontSize={13} fontWeight="700" color={colors.text}>
                  {label}
                </Text>
              </XStack>
              {view === "success" && successInfo?.time ? (
                <XStack jc="space-between">
                  <Text fontSize={13} color={colors.sub}>
                    時間
                  </Text>
                  <Text fontSize={13} fontWeight="700" color={colors.text}>
                    {successInfo.time}
                  </Text>
                </XStack>
              ) : null}
            </YStack>
            {justPaid ? <OaRedirectCountdown url={status?.pay_link.oa_chat_url} supplierName={status?.pay_link.supplier_name} /> : null}
            {justPaid && viewer === "non_member" && pt && cusJwtToken ? <JoinFamilyCard payToken={pt} cusJwtToken={cusJwtToken} /> : null}
            {offerRegister ? <RegisterBenefitsCard /> : null}
            {showAddFriend && status?.pay_link.add_friend_url ? (
              <AddFriendCard supplierName={status.pay_link.supplier_name} url={status.pay_link.add_friend_url} />
            ) : null}
            {/* 底部固定按鈕列的高度，避免內容被蓋住 */}
            <View h={offerRegister ? 150 : 90} />
          </YStack>
        </RadiusContentWrapper>
        {/* 按鈕置底（2026-10-20 使用者要求），跟付款前的「付款」按鈕同一個位置 */}
        <YStack
          px="$4"
          pt="$3"
          gap="$2.5"
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            bottom: 0,
            paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
            background: `linear-gradient(to top, ${colors.bg} 80%, rgba(245, 247, 251, 0))`,
          }}
        >
          {offerRegister ? (
            // 付款完成後的可選註冊（2026-10-20 使用者選 A＋D）：主按鈕註冊、「回到 LINE」降為次要
            <YStack width="100%" gap="$2.5">
              <a href={`https://liff.line.me/${registerLiffId}`} style={{ width: "100%", textDecoration: "none" }}>
                <YStack br={14} py="$3.5" ai="center" className="primary-bg-gradient" cursor="pointer" pressStyle={{ opacity: 0.85 }}>
                  <Text fontSize={15} fontWeight="800" color={colors.light}>
                    免費註冊帳號
                  </Text>
                </YStack>
              </a>
              {returnUrl ? (
                <a href={returnUrl} style={{ width: "100%", textDecoration: "none" }}>
                  <YStack
                    br={14}
                    py="$3.5"
                    ai="center"
                    bg={colors.light}
                    bw={1.5}
                    bc={V2_SOFT_BORDER}
                    cursor="pointer"
                    pressStyle={{ opacity: 0.85 }}
                  >
                    <Text fontSize={15} fontWeight="800" color={colors.primary}>
                      回到 LINE
                    </Text>
                  </YStack>
                </a>
              ) : null}
            </YStack>
          ) : returnUrl ? (
            <a href={returnUrl} style={{ width: "100%", textDecoration: "none" }}>
              <YStack br={12} py="$3.5" ai="center" className="primary-bg-gradient" cursor="pointer" pressStyle={{ opacity: 0.85 }}>
                <Text fontSize={15} fontWeight="800" color={colors.light}>
                  回到 LINE
                </Text>
              </YStack>
            </a>
          ) : (
            <Text fontSize={12} color={colors.hint} ta="center">
              可以關閉此頁
            </Text>
          )}
        </YStack>
      </YStack>
    );
  }

  // view === "payable"：說明卡（這頁在做什麼）→ 付給誰／付什麼 →（無付款權限提示）→ 信用卡 → Apple Pay，
  // 付款按鈕固定在底部（2026-10-19 使用者選 A＋C 版）
  if (!status) return null;
  const { pay_link } = status;
  const amount = status.amount ?? 0;
  const cardBusy = cardSubmitting || cardState.gettingPrime;
  const canPayCard = cardState.canPay && !cardSubmitting;
  const ipassEnabled = !!pay_link.ipass_twqr_enabled;
  const ipassFirst = ipassEnabled && status.payment_method === "ipass_twqr";
  const cardSection = (
    <YStack bg={colors.light} bw={1} bc={colors.borderLight} br={14} p="$4" gap="$3">
      <Text fontSize={12} fontWeight="700" color={colors.sub} letterSpacing={0.5}>
        信用卡
      </Text>
      <OneTimeCardForm ref={cardRef} onPrime={onCardPrime} onStateChange={onCardStateChange} />
    </YStack>
  );
  const ipassCard = ipassEnabled ? (
    <YStack bg={colors.light} bw={1} bc={colors.borderLight} br={14} p="$4" gap="$3">
      <XStack ai="center" gap="$2.5">
        <View w={42} h={28} br={6} bw={1} bc="#e6e9f0" bg={colors.light} ai="center" jc="center" overflow="hidden">
          <img src={ipassMoneyLogo} alt="iPASS MONEY" style={{ width: 34, height: 20, objectFit: "contain" }} />
        </View>
        <YStack flex={1}>
          <Text fontSize={12} fontWeight="700" color={colors.sub} letterSpacing={0.5}>
            一卡通 MONEY
          </Text>
          <Text fontSize={12.5} color={colors.sub} lineHeight={18}>
            手機會直接開啟 iPASS MONEY APP 付這一筆，付完自動回到這頁；在電腦上會顯示付款碼，請用 APP 掃描
          </Text>
        </YStack>
      </XStack>
      <YStack
        br={12}
        py="$3"
        ai="center"
        bg={ipassSubmitting ? colors.border : "#1E5EC8"}
        cursor={ipassSubmitting ? "default" : "pointer"}
        pressStyle={{ opacity: ipassSubmitting ? 1 : 0.85 }}
        onPress={onIpassPay}
        role="button"
        aria-disabled={ipassSubmitting}
      >
        <Text fontSize={15} fontWeight="800" color={ipassSubmitting ? colors.hint : colors.light}>
          {ipassSubmitting ? "開啟中…" : `用 iPASS MONEY 付款 NT$ ${amount.toLocaleString()}`}
        </Text>
      </YStack>
    </YStack>
  ) : null;
  return (
    <YStack height="100dvh" width="100vw" background={colors.bg}>
      {headerNode("付款", `${pay_link.supplier_name} · 訂單 ${pay_link.display_id}`)}
      <RadiusContentWrapper>
        {/* RadiusContentWrapper 的 space 在這頁不會產生間距（卡片會黏在一起），自己包一層用 gap */}
        <YStack gap={16}>
          <XStack bg={colors.primaryBg} bw={1} bc="#dbeafe" br={14} px="$4" py="$3.5" gap="$3" ai="flex-start">
            <View w={34} h={34} br={10} bg={colors.primary} ai="center" jc="center" flexShrink={0}>
              <RiShieldCheckLine size={19} color={colors.light} />
            </View>
            <YStack flex={1} gap="$1">
              <Text fontSize={14.5} fontWeight="800" color={colors.primaryText}>
                幫這筆瓦斯訂單付款
              </Text>
              <Text fontSize={12.5} color={colors.sub} lineHeight={20}>
                {ipassFirst
                  ? "配送員已請款並改為一卡通 MONEY 付款，也可以改用信用卡。"
                  : `配送員已請款，請用${ipassEnabled ? "信用卡或一卡通 MONEY" : "信用卡"}付這筆款項。`}
                不用註冊、不用加入家庭，付完瓦斯行會立刻收到通知。
              </Text>
            </YStack>
          </XStack>

          <YStack bg={colors.light} bw={1} bc={colors.borderLight} br={14} p="$4" gap="$3">
            <Text fontSize={12} fontWeight="700" color={colors.sub} letterSpacing={0.5}>
              付給
            </Text>
            <Text fontSize={17} fontWeight="800" color={colors.text}>
              {pay_link.supplier_name}
            </Text>
            <YStack gap="$1">
              {pay_link.items.map((it, idx) => {
                const title = it.kind === "gas" ? formatGasTitle(it.gas_type, it.gas_kilogram ?? "") : (it.commodity_name ?? "商品");
                const qty = it.kind === "gas" ? it.numbers_of_cylinder : it.numbers_of_commodity;
                return (
                  <Text key={idx} fontSize={13.5} color={colors.sub}>
                    {title} × {qty ?? 1}
                  </Text>
                );
              })}
            </YStack>
            <XStack jc="space-between" ai="baseline" pt="$3" btw={1} btc={colors.borderLight}>
              <Text fontSize={13} color={colors.sub}>
                應付金額
              </Text>
              <Text fontSize={24} fontWeight="800" color={colors.primary} style={{ fontVariantNumeric: "tabular-nums" }}>
                NT$ {amount.toLocaleString()}
              </Text>
            </XStack>
          </YStack>

          {viewer === "member_no_pay" ? (
            <Text fontSize={12.5} lineHeight={19} color={colors.amberDark} bg={colors.bgAmber} br={10} px="$3" py="$2.5">
              你在這個帳戶沒有付款權限，可以直接輸入卡號付這一筆。
            </Text>
          ) : null}

          {/*
           * 付款方式的排序：配送員現場把這筆改成一卡通（payment_method=ipass_twqr）的單，一卡通排最前面、
           * 信用卡退成「或」；其他單維持信用卡在前。兩種都付得了，只是把配送員跟客戶講好的那個放前面。
           */}
          {ipassFirst ? ipassCard : cardSection}

          {ipassEnabled ? (
            <YStack gap="$2">
              <Text fontSize={12} color={colors.hint} ta="center">
                — 或 —
              </Text>
              {ipassFirst ? cardSection : ipassCard}
            </YStack>
          ) : null}

          {ap.state !== "unavailable" ? (
            <YStack gap="$2">
              <Text fontSize={12} color={colors.hint} ta="center">
                — 或 —
              </Text>
              <ApplePayButton state={submitting ? "paying" : ap.state} onPress={onApplePay} />
            </YStack>
          ) : showApReason && ap.reason ? (
            <Text fontSize={11} color={colors.hint} ta="center">
              [dev] Apple Pay：{ap.reason}
            </Text>
          ) : null}

          {/* 底部固定付款列的高度（含請款鎖倒數那一行），避免最後一張卡被蓋住 */}
          <View h={lockRemaining !== null ? 135 : 110} />
        </YStack>
      </RadiusContentWrapper>

      <YStack
        px="$4"
        pt="$3"
        gap="$2"
        style={{
          // Tamagui 的 position 不接受 fixed，直接寫在 style（web）
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          paddingBottom: "calc(16px + env(safe-area-inset-bottom, 0px))",
          background: `linear-gradient(to top, ${colors.bg} 75%, rgba(245, 247, 251, 0))`,
        }}
      >
        {lockRemaining !== null ? <PaymentLockCountdownNote remainingMs={lockRemaining} /> : null}
        <YStack
          br={12}
          py="$3.5"
          ai="center"
          className={canPayCard ? "primary-bg-gradient" : undefined}
          bg={canPayCard ? undefined : colors.border}
          cursor={canPayCard ? "pointer" : "default"}
          pressStyle={{ opacity: canPayCard ? 0.85 : 1 }}
          onPress={() => cardRef.current?.pay()}
          role="button"
          aria-disabled={!canPayCard}
        >
          <Text fontSize={15} fontWeight="800" color={canPayCard ? colors.light : colors.hint}>
            {cardBusy ? "處理中…" : !cardState.sdkReady ? "載入安全欄位…" : `付款 NT$ ${amount.toLocaleString()}`}
          </Text>
        </YStack>
        <Text fontSize={11.5} color={colors.hint} ta="center">
          {canPayCard ? "按下後可能會跳到銀行頁面驗證" : "填完卡號、有效期限與安全碼後才能按"}
        </Text>
      </YStack>
    </YStack>
  );
};

/** LINE 付款頁 Skin V2 的淡藍（見 GasShop/skin/SkinV2.tsx 的 v2） */
const V2_SOFT = "#EAF4FE";
const V2_SOFT_BORDER = "#BBDCFA";

/**
 * 付款完成後給未註冊訪客看的註冊好處（2026-10-20 使用者選 A＋D）：白卡圓角 24、三行好處，
 * 按鈕在頁面底部（主按鈕「免費註冊帳號」、次要「回到 LINE」）。註冊的是自己的帳號，不加入付款的這個家庭。
 */
const RegisterBenefitsCard = () => {
  const row = (icon: React.ReactNode, text: string) => (
    <XStack ai="center" gap="$2.5">
      <View w={30} h={30} br={10} bg={V2_SOFT} ai="center" jc="center" flexShrink={0}>
        {icon}
      </View>
      <Text fontSize={13} color={colors.text}>
        {text}
      </Text>
    </XStack>
  );
  return (
    <YStack width="100%" bg={colors.light} br={24} px={18} py={18} gap="$3" style={{ boxShadow: "0 2px 10px -4px rgba(21, 101, 192, 0.10)" }}>
      <Text fontSize={15.5} fontWeight="800" color={colors.text}>
        加入我們，叫瓦斯更快速！
      </Text>
      {row(<RiFileList3Line size={16} color={colors.primary} />, "在 LINE 直接叫瓦斯、查訂單")}
      {row(<RiBankCardLine size={16} color={colors.primary} />, "綁卡後付款一鍵完成，不用再輸入卡號")}
      {row(<RiNotification3Line size={16} color={colors.primary} />, "配送、付款進度直接通知你")}
    </YStack>
  );
};

export default PayLinkView;
